import React from "react";
import DataSourceContainer from "../components/DataSourceContainer";
import { observer } from "mobx-react";
import { DataSourcesStore } from "../stores/DataSourcesStore";
import { v4 as uuidv4 } from "uuid";
import { Box, Typography } from "@mui/material";
import theme from "Theme/AppTheme";
import MappedValue from "./MappedValue";
import AddButton from "../components/AddButton";
import { emptyMappedValue } from "../helpers/emptyStates";

type ValueMappingProps = {
    store: DataSourcesStore;
};

const ValueMapping: React.FC<ValueMappingProps> = observer(({ store }) => {
    const { sourceFields, mappedValues, setMappedValues } = store;

    const targetOptions = sourceFields
        .filter((field) => field.targetFieldName)
        .map((field) => ({
            label: field.targetFieldName,
            value: field.targetFieldName,
        }));

    const updatedTargetField = (value: string, id: string) => {
        setMappedValues(
            mappedValues.map((mappedValue) => {
                if (mappedValue.id === id) {
                    return { ...mappedValue, targetField: value };
                }
                return mappedValue;
            }),
        );
    };

    const updatedMappedPair = (
        key: string,
        value: string,
        id: string,
        mappedValueId: string,
    ) => {
        setMappedValues(
            mappedValues.map((mappedValue) => {
                if (mappedValue.id === mappedValueId) {
                    return {
                        ...mappedValue,
                        pairs: mappedValue.pairs.map((pair) => {
                            if (pair.id === id) {
                                return { ...pair, [key]: value };
                            }
                            return pair;
                        }),
                    };
                }
                return mappedValue;
            }),
        );
    };

    const addMappedPair = (id: string) => {
        setMappedValues(
            mappedValues.map((mappedValue) => {
                if (mappedValue.id === id) {
                    return {
                        ...mappedValue,
                        pairs: [
                            ...mappedValue.pairs,
                            {
                                sourceValue: "",
                                targetValue: "",
                                id: uuidv4(),
                            },
                        ],
                    };
                }
                return mappedValue;
            }),
        );
    };

    const removeMappedPair = (id: string, mappedValueId: string) => {
        setMappedValues(
            mappedValues.map((mappedValue) => {
                if (mappedValue.id === mappedValueId) {
                    return {
                        ...mappedValue,
                        pairs: mappedValue.pairs.filter(
                            (pair) => pair.id !== id,
                        ),
                    };
                }
                return mappedValue;
            }),
        );
    };

    const addMappedValue = () => {
        setMappedValues([...mappedValues, emptyMappedValue()]);
    };

    const removeMappedValue = (id: string) => {
        setMappedValues(
            mappedValues.filter((mappedValue) => mappedValue.id !== id),
        );
    };

    return (
        <DataSourceContainer
            heading="Define Value Mapping"
            subHeading="Define the distinct values sent to Authenticx to ensure consistent reporting"
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    background: theme.palette.gray[100],
                    padding: 2,
                    borderRadius: 2,
                }}
            >
                {!!mappedValues.length && (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                marginLeft: 1,
                            }}
                        >
                            <Typography
                                sx={{ fontWeight: 600, fontSize: 14, flex: 1 }}
                            >
                                Target Field
                            </Typography>
                            <Box
                                sx={{ display: "flex", flex: 3, marginLeft: 6 }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: 14,
                                        flex: 1,
                                    }}
                                >
                                    Source Value
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: 14,
                                        flex: 1,
                                        marginLeft: -5,
                                    }}
                                >
                                    Target Value
                                </Typography>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            {mappedValues.map((mappedValue) => {
                                return (
                                    <MappedValue
                                        key={mappedValue.id}
                                        mappedValue={mappedValue}
                                        targetOptions={targetOptions}
                                        updatedTargetField={(value) =>
                                            updatedTargetField(
                                                value,
                                                mappedValue.id,
                                            )
                                        }
                                        updatedMappedPair={(key, value, id) =>
                                            updatedMappedPair(
                                                key,
                                                value,
                                                id,
                                                mappedValue.id,
                                            )
                                        }
                                        addMappedPair={() =>
                                            addMappedPair(mappedValue.id)
                                        }
                                        removeMappedPair={(id) =>
                                            removeMappedPair(id, mappedValue.id)
                                        }
                                        removeMappedValue={() =>
                                            removeMappedValue(mappedValue.id)
                                        }
                                    />
                                );
                            })}
                        </Box>
                    </>
                )}
                <AddButton onClick={addMappedValue}>Add Mapping</AddButton>
            </Box>
        </DataSourceContainer>
    );
});

export default ValueMapping;
